import { useEffect, useRef, useState } from 'react'
import { useI18n } from '../../hooks/useI18n'

/**
 * ONE field that opens ONE sheet with two wheels — the web twin of the mini
 * program's `<Picker mode="date" fields="month">`.
 *
 * Three attempts, for the record, because each failed for a different reason:
 *
 *   1. a text input          — people typed "13/04/1990" and Postgres refused it
 *   2. <input type="month">  — MDN calls support "patchy", and where it is
 *                              missing the browser SILENTLY degrades to a text
 *                              box, i.e. straight back to (1)
 *   3. two <select>s         — reliably native, but two boxes where WeChat has
 *                              one field, and visibly uglier
 *
 * So: a read-only field that opens a bottom sheet containing two scroll
 * columns. CSS scroll-snap does the wheel feel with no library and no
 * dependency on a browser control that might not exist. Nothing here can fall
 * back to free text, which was the whole problem.
 */
export default function MonthYearPicker({
  value, onChange, minYear = 1920, maxYear = new Date().getFullYear(), defaultYearsAgo = 20
}: {
  value: unknown
  onChange: (value: string) => void
  minYear?: number
  maxYear?: number
  defaultYearsAgo?: number
}) {
  const { lang } = useI18n()
  const [open, setOpen] = useState(false)

  const [storedYear, storedMonth] = value ? String(value).split('-') : ['', '']
  // Where the wheels OPEN when nothing is chosen: about the age of a plausible
  // member, not the edge of the range. Same reasoning as the mini program —
  // starting at this year means scrolling back two decades every time.
  const fallbackYear = String(maxYear - defaultYearsAgo)
  const [year, setYear] = useState(storedYear || fallbackYear)
  const [month, setMonth] = useState(storedMonth || '01')

  const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => String(maxYear - i))
  const months = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'))

  const monthLabel = (m: string) =>
    lang === 'zh' ? `${Number(m)} 月` : new Date(2000, Number(m) - 1).toLocaleString('en', { month: 'long' })

  // Reopening must show what is currently chosen, not the last thing scrolled.
  useEffect(() => {
    if (!open) return
    setYear(storedYear || fallbackYear)
    setMonth(storedMonth || '01')
  }, [open])

  // The sheet sits over the page, so the page must not scroll behind it.
  useEffect(() => {
    if (!open) return undefined
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previous }
  }, [open])

  const display = storedYear && storedMonth
    ? `${storedYear} · ${monthLabel(storedMonth)}`
    : (lang === 'zh' ? '选择年月' : 'Select month and year')

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`flex h-12 w-full items-center justify-between rounded-sm border border-gold-dim bg-card px-3 text-left text-md ${
          storedYear ? 'text-ivory' : 'text-muted-2'
        }`}
      >
        <span>{display}</span>
        <span className="text-gold">▾</span>
      </button>

      {open ? (
        <div className="fixed inset-0 z-[200] flex flex-col justify-end bg-black/85">
          {/* Tapping the dimmed area closes without choosing — the standard
              expectation for a sheet, and the only visible way out on a phone
              if the buttons are missed. */}
          <button type="button" aria-label="Close" className="flex-1" onClick={() => setOpen(false)} />

          <div
            className="mx-auto w-full max-w-app rounded-t-md border-t border-gold-dim bg-card"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            <div className="flex items-center justify-between border-b border-gold-dim px-4 py-3">
              <button type="button" onClick={() => setOpen(false)} className="text-13 text-muted">
                {lang === 'zh' ? '取消' : 'Cancel'}
              </button>
              <span className="text-13 text-muted-2">{lang === 'zh' ? '出生年月' : 'Month and year'}</span>
              <button
                type="button"
                onClick={() => { onChange(`${year}-${month}-01`); setOpen(false) }}
                className="text-13 font-bold text-gold"
              >
                {lang === 'zh' ? '确定' : 'Done'}
              </button>
            </div>

            <div className="relative flex">
              {/* The selection band, so the middle row reads as "chosen" the
                  way a native wheel does. */}
              <div className="pointer-events-none absolute inset-x-0 top-1/2 h-10 -translate-y-1/2 border-y border-gold-dim" />
              <Wheel items={years} value={year} onSelect={setYear} render={(y) => y} />
              <Wheel items={months} value={month} onSelect={setMonth} render={monthLabel} />
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}

/**
 * One scrolling column. Snap points do the wheel behaviour; the padding above
 * and below is what lets the first and last item reach the centre band.
 */
function Wheel({
  items, value, onSelect, render
}: { items: string[]; value: string; onSelect: (v: string) => void; render: (v: string) => string }) {
  const ref = useRef<HTMLDivElement>(null)

  // Open ON the current value rather than at the top — otherwise choosing 1990
  // means scrolling past thirty-odd years every single time.
  useEffect(() => {
    const container = ref.current
    if (!container) return
    const index = items.indexOf(value)
    if (index >= 0) container.scrollTop = index * 40
  }, [])

  return (
    <div
      ref={ref}
      className="h-40 flex-1 snap-y snap-mandatory overflow-y-auto"
      style={{ scrollbarWidth: 'none' }}
    >
      <div className="h-[60px]" />
      {items.map((item) => (
        <button
          key={item}
          type="button"
          onClick={() => onSelect(item)}
          className={`flex h-10 w-full snap-center items-center justify-center text-md ${
            item === value ? 'font-bold text-ivory' : 'text-muted-2'
          }`}
        >
          {render(item)}
        </button>
      ))}
      <div className="h-[60px]" />
    </div>
  )
}
